import React, { useEffect, useState } from "react";
import { api, buildMediaUrl, formatApiErrorDetail } from "../../lib/api";
import { useLang } from "../../contexts/LanguageContext";
import { toast } from "sonner";
import { Trash2, Copy, Upload } from "lucide-react";

export default function AdminMedia() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/media", { params: { limit: 100 } });
    setItems(data.items || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const kind = file.type.startsWith("image/") ? "image" : file.type.startsWith("video/") ? "video" : file.type === "application/pdf" ? "pdf" : "other";
      fd.append("kind", kind);
      await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t.admin.uploaded);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/media/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div data-testid="admin-media">
      <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow text-ink mb-2">{t.admin.media}</div>
          <h1 className="heading-serif text-4xl text-ink">{t.sections.media_title}</h1>
        </div>
        <label className="bg-ink text-white h-11 px-5 text-[12px] uppercase tracking-[0.2em] font-medium hover:bg-ink/90 inline-flex items-center gap-2 cursor-pointer">
          <Upload className="w-3.5 h-3.5" /> {t.admin.upload}
          <input data-testid="admin-media-upload" type="file" onChange={onUpload} disabled={busy} className="hidden" />
        </label>
      </div>

      {loading ? (
        <div className="text-slate500 py-16 text-center">{t.common.loading}</div>
      ) : items.length === 0 ? (
        <div className="text-slate500 py-16 text-center" data-testid="admin-media-empty">{t.common.no_results}</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {items.map((m) => (
            <div key={m.id} className="bg-white border border-slate200 overflow-hidden" data-testid={`media-tile-${m.id}`}>
              <div className="aspect-square bg-slate50">
                {m.kind === "image" || (m.content_type || "").startsWith("image/") ? (
                  <img src={buildMediaUrl(m.public_url)} alt="" className="w-full h-full object-cover" />
                ) : m.kind === "video" || (m.content_type || "").startsWith("video/") ? (
                  <video src={buildMediaUrl(m.public_url)} className="w-full h-full object-cover" />
                ) : (
                  <div className="grid place-items-center h-full text-[11px] uppercase tracking-widest text-slate500">{m.kind}</div>
                )}
              </div>
              <div className="p-3 text-[11px] text-slate600">
                <div className="truncate">{m.original_filename}</div>
                <div className="flex items-center justify-between mt-2">
                  <button
                    onClick={() => { navigator.clipboard.writeText(m.public_url); toast.success("Copied"); }}
                    className="text-[10px] uppercase tracking-widest hover:text-ink inline-flex items-center gap-1"
                  >
                    <Copy className="w-3 h-3" /> {t.admin.copy_url}
                  </button>
                  <button onClick={() => remove(m.id)} className="text-[10px] uppercase tracking-widest hover:text-crimson inline-flex items-center gap-1">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
